import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import {
    RiShieldUserLine,
    RiKey2Line,
    RiUserShared2Fill,
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiCheckLine,
    RiEyeLine,
    RiShieldCheckLine,
    RiBuildingLine,
    RiLock2Line
} from 'react-icons/ri';
import API_BASE_URL from '../../../config/api';
import Topbar from '../mini-comp/Topbar';
import Sidebar from '../mini-comp/Sidebar';

const COLOR_PALETTES = [
    { bg: 'bg-primary-subtle', text: 'text-primary', badge: 'bg-primary', border: 'border-primary-subtle', gradient: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)' },
    { bg: 'bg-success-subtle', text: 'text-success', badge: 'bg-success', border: 'border-success-subtle', gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' },
    { bg: 'bg-info-subtle', text: 'text-info', badge: 'bg-info', border: 'border-info-subtle', gradient: 'linear-gradient(135deg, #0284c7 0%, #06b6d4 100%)' },
    { bg: 'bg-warning-subtle', text: 'text-warning', badge: 'bg-warning', border: 'border-warning-subtle', gradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)' },
    { bg: 'bg-danger-subtle', text: 'text-danger', badge: 'bg-danger', border: 'border-danger-subtle', gradient: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)' },
];

const Roles = () => {
    const [roles, setRoles] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [loading, setLoading] = useState(true);

    // View Modal State
    const [viewRole, setViewRole] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    // Create / Edit Modal State
    const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        codename: '',
        permission_codenames: []
    });
    const [submitting, setSubmitting] = useState(false);
    const [permSearch, setPermSearch] = useState('');

    const fetchRoles = async () => {
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/v1/users/roles/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setRoles(response.data);
        } catch (error) {
            console.error('Error fetching roles:', error);
            Swal.fire({
                icon: 'error',
                title: 'Failed to fetch roles',
                text: error.response?.data?.detail || 'Could not load roles from server.'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchAllPermissions = async () => {
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/v1/users/permissions/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log('Permissions:', response.data);
            setAllPermissions(response.data);
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    useEffect(() => {
        fetchRoles();
        fetchAllPermissions();
    }, []);

    // --- READ DETAILS ---
    const handleViewRole = async (roleId) => {
        setViewLoading(true);
        setViewRole(null);
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/v1/users/roles/${roleId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setViewRole(response.data);
        } catch (error) {
            console.error('Error fetching role details:', error);
            Swal.fire('Error', 'Failed to fetch role details.', 'error');
        } finally {
            setViewLoading(false);
        }
    };

    // --- CREATE HANDLER ---
    const openCreateModal = () => {
        if (allPermissions.length === 0) {
            fetchAllPermissions();
        }
        setFormData({
            name: '',
            codename: '',
            permission_codenames: []
        });
        setEditingRoleId(null);
        setModalMode('create');
        setPermSearch('');
    };

    // --- EDIT HANDLER ---
    const openEditModal = (role, e) => {
        if (e) e.stopPropagation();
        if (allPermissions.length === 0) {
            fetchAllPermissions();
        }
        setFormData({
            name: role.name || '',
            codename: role.codename || '',
            permission_codenames: role.permissions ? role.permissions.map(p => p.codename) : []
        });
        setEditingRoleId(role.id);
        setModalMode('edit');
        setPermSearch('');
    };

    // --- TOGGLE PERMISSION CHECKBOX ---
    const togglePermission = (codename) => {
        setFormData(prev => {
            const exists = prev.permission_codenames.includes(codename);
            return {
                ...prev,
                permission_codenames: exists
                    ? prev.permission_codenames.filter(c => c !== codename)
                    : [...prev.permission_codenames, codename]
            };
        });
    };

    const selectAllPermissions = () => {
        setFormData(prev => ({
            ...prev,
            permission_codenames: allPermissions.map(p => p.codename)
        }));
    };

    const deselectAllPermissions = () => {
        setFormData(prev => ({
            ...prev,
            permission_codenames: []
        }));
    };

    // --- SUBMIT CREATE / EDIT ---
    const handleSubmitForm = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.codename.trim()) {
            Swal.fire('Validation Error', 'Role Name and Codename are required.', 'warning');
            return;
        }

        setSubmitting(true);
        const token = Cookies.get('token');

        try {
            if (modalMode === 'create') {
                await axios.post(`${API_BASE_URL}/api/v1/users/roles/`, formData, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire({
                    icon: 'success',
                    title: 'Role Created!',
                    text: `Role "${formData.name}" has been created successfully.`,
                    timer: 2000,
                    showConfirmButton: false
                });
            } else if (modalMode === 'edit') {
                await axios.patch(`${API_BASE_URL}/api/v1/users/roles/${editingRoleId}/`, formData, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire({
                    icon: 'success',
                    title: 'Role Updated!',
                    text: `Role "${formData.name}" has been updated successfully.`,
                    timer: 2000,
                    showConfirmButton: false
                });
            }

            setModalMode(null);
            fetchRoles();
        } catch (error) {
            console.error('Error saving role:', error);
            const errMsg = error.response?.data?.detail
                || error.response?.data?.codename?.[0]
                || error.response?.data?.name?.[0]
                || 'Failed to save role.';
            Swal.fire('Error', errMsg, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // --- DELETE HANDLER ---
    const handleDeleteRole = async (role, e) => {
        if (e) e.stopPropagation();

        const result = await Swal.fire({
            title: `Delete Role "${role.name}"?`,
            text: "This action cannot be undone. Users with this role must be reassigned.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token');
                await axios.delete(`${API_BASE_URL}/api/v1/users/roles/${role.id}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Deleted!', `Role "${role.name}" has been deleted.`, 'success');
                fetchRoles();
            } catch (error) {
                console.error('Error deleting role:', error);
                const errMsg = error.response?.data?.detail || 'Failed to delete role.';
                Swal.fire('Delete Failed', errMsg, 'error');
            }
        }
    };

    // Filter permissions for modal search
    const filteredPermissions = allPermissions.filter(p =>
        p.name.toLowerCase().includes(permSearch.toLowerCase()) ||
        p.codename.toLowerCase().includes(permSearch.toLowerCase())
    );

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">
                            {/* Page Header Card */}
                            <div className="col-xl-12 mb-4">
                                <div className="card border-0 shadow-sm overflow-hidden">
                                    <div className="card-body p-4 bg-gradient-primary text-white position-relative" style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                    }}>
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                            <div>
                                                <h3 className="fw-bold text-white mb-1 d-flex align-items-center gap-2">
                                                    <RiUserShared2Fill className="fs-2" /> Roles & Permissions Management
                                                </h3>
                                                <p className="text-white-50 mb-0 fs-14">
                                                    Configure user access levels and fine-grained permissions across your organisation.
                                                </p>
                                            </div>
                                            <div className="d-flex align-items-center gap-3">
                                                <span className="badge bg-white text-primary fs-14 px-3 py-2 rounded-pill fw-bold shadow-sm">
                                                    {roles.length} Active Roles
                                                </span>
                                                <button
                                                    className="btn btn-light text-primary fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2"
                                                    onClick={openCreateModal}
                                                >
                                                    <RiAddLine className="fs-5" /> Add New Role
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Roles Card Grid */}
                            {loading ? (
                                <div className="col-12 text-center py-5">
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                        <span className="visually-hidden">Loading roles...</span>
                                    </div>
                                    <p className="text-muted mt-3 fs-15">Loading roles & permissions...</p>
                                </div>
                            ) : roles.length > 0 ? (
                                roles.map((role, idx) => {
                                    const palette = COLOR_PALETTES[idx % COLOR_PALETTES.length];
                                    return (
                                        <div key={role.id} className="col-xxl-3 col-xl-4 col-lg-6 col-md-6 mb-4">
                                            <div
                                                className={`card h-100 shadow-sm border ${palette.border} transition-all hover-lift`}
                                                style={{ borderRadius: '14px', overflow: 'hidden', cursor: 'pointer' }}
                                                onClick={() => handleViewRole(role.id)}
                                            >
                                                {/* Top Color Accent Bar */}
                                                <div style={{ height: '5px', background: palette.gradient }}></div>

                                                <div className="card-body d-flex flex-column p-4">
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <div className={`p-3 rounded-3 ${palette.bg} ${palette.text} d-flex align-items-center justify-content-center`}>
                                                            <RiShieldCheckLine className="fs-3" />
                                                        </div>
                                                        <span className="badge bg-light text-dark border px-2 py-1 fs-12 fw-semibold">
                                                            ID: #{role.id}
                                                        </span>
                                                    </div>

                                                    <h5 className="fw-bold mb-1 text-dark">{role.name}</h5>
                                                    <div className="mb-3">
                                                        <span className={`badge ${palette.bg} ${palette.text} border ${palette.border} px-2 py-1 fs-12 font-monospace`}>
                                                            {role.codename}
                                                        </span>
                                                    </div>

                                                    <div className="mt-auto pt-3 border-top d-flex align-items-center justify-content-between">
                                                        <div className="d-flex align-items-center gap-1 text-muted fs-13">
                                                            <RiKey2Line className={palette.text} />
                                                            <span className="fw-semibold text-dark">{role.permissions?.length || 0}</span> Permissions
                                                        </div>

                                                        <div className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary p-1 px-2 border-0 bg-primary-subtle text-primary"
                                                                title="View Details"
                                                                onClick={() => handleViewRole(role.id)}
                                                            >
                                                                <RiEyeLine size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-warning p-1 px-2 border-0 bg-warning-subtle text-warning"
                                                                title="Edit Role"
                                                                onClick={(e) => openEditModal(role, e)}
                                                            >
                                                                <RiEditLine size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger p-1 px-2 border-0 bg-danger-subtle text-danger"
                                                                title="Delete Role"
                                                                onClick={(e) => handleDeleteRole(role, e)}
                                                            >
                                                                <RiDeleteBinLine size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="col-12 text-center py-5">
                                    <div className="card p-5 border-0 shadow-sm text-center" style={{ borderRadius: '16px' }}>
                                        <RiShieldUserLine size={56} className="text-primary mb-3 mx-auto opacity-75" />
                                        <h4 className="fw-bold text-dark">No Roles Found</h4>
                                        <p className="text-muted fs-15">You haven't created any custom roles yet.</p>
                                        <div>
                                            <button className="btn btn-primary fw-bold px-4 py-2" onClick={openCreateModal}>
                                                <RiAddLine className="fs-5 me-1" /> Create Your First Role
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* --- READ DETAIL MODAL --- */}
            {(viewLoading || viewRole) && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1050 }}
                    onClick={(e) => { if (e.target === e.currentTarget) setViewRole(null); }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                            <div className="modal-header bg-primary text-white p-4" style={{
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                            }}>
                                <div>
                                    <h5 className="modal-title fw-bold text-white d-flex align-items-center gap-2 mb-1">
                                        <RiShieldCheckLine className="fs-3" />
                                        {viewRole?.name || 'Loading Role...'}
                                    </h5>
                                    {viewRole && (
                                        <span className="badge bg-white text-primary font-monospace fs-12">
                                            Codename: {viewRole.codename}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setViewRole(null)}
                                />
                            </div>

                            <div className="modal-body p-4" style={{ overflowY: 'auto', flex: 1 }}>
                                {viewLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading details...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Fetching role details...</p>
                                    </div>
                                ) : viewRole ? (
                                    <div>
                                        {/* Meta Stats Row */}
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-4">
                                                <div className="p-3 bg-primary-subtle rounded-3 border border-primary-subtle text-center">
                                                    <small className="text-primary fw-bold text-uppercase d-block mb-1">Role ID</small>
                                                    <span className="fw-bold text-primary fs-18">#{viewRole.id}</span>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="p-3 bg-info-subtle rounded-3 border border-info-subtle text-center">
                                                    <small className="text-info fw-bold text-uppercase d-block mb-1">Company Scope</small>
                                                    <span className="fw-bold text-info fs-18 d-flex align-items-center justify-content-center gap-1">
                                                        <RiBuildingLine />
                                                        {viewRole.company ? `Company #${viewRole.company}` : 'Global'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="p-3 bg-success-subtle rounded-3 border border-success-subtle text-center">
                                                    <small className="text-success fw-bold text-uppercase d-block mb-1">Assigned Permissions</small>
                                                    <span className="fw-bold text-success fs-18 d-flex align-items-center justify-content-center gap-1">
                                                        <RiKey2Line />
                                                        {viewRole.permissions?.length || 0} Permissions
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Permissions List */}
                                        <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                            <RiLock2Line className="text-primary" /> Permission Assignments
                                        </h6>

                                        {viewRole.permissions && viewRole.permissions.length > 0 ? (
                                            <div className="row g-2">
                                                {viewRole.permissions.map((perm) => (
                                                    <div key={perm.id} className="col-md-6">
                                                        <div className="p-3 border rounded-3 h-100 bg-light d-flex align-items-center gap-3">
                                                            <div className="p-2 bg-success text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '28px', height: '28px' }}>
                                                                <RiCheckLine size={16} />
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold text-dark fs-14">{perm.name}</div>
                                                                <code className="text-primary fs-12">{perm.codename}</code>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 bg-light rounded-3 border text-muted">
                                                <RiKey2Line size={32} className="text-muted mb-2 opacity-50" />
                                                <p className="mb-0">No permissions assigned to this role.</p>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>

                            <div className="modal-footer bg-light p-3">
                                {viewRole && (
                                    <button
                                        type="button"
                                        className="btn btn-warning fw-bold px-3 me-auto d-flex align-items-center gap-1"
                                        onClick={(e) => {
                                            const roleToEdit = viewRole;
                                            setViewRole(null);
                                            openEditModal(roleToEdit, e);
                                        }}
                                    >
                                        <RiEditLine /> Edit Role
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-secondary fw-bold px-4"
                                    onClick={() => setViewRole(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CREATE / EDIT ROLE MODAL --- */}
            {modalMode && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ backgroundColor: 'rgba(176, 177, 178, 0.55)', zIndex: 1050 }}
                    onClick={(e) => { if (e.target === e.currentTarget) setModalMode(null); }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
                        <form 
                            className="modal-content border-0 shadow-lg" 
                            style={{ borderRadius: '16px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
                            onSubmit={handleSubmitForm}
                        >
                            <div className="modal-header bg-primary text-white p-4" style={{
                                background: modalMode === 'create'
                                    ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                                    : 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)'
                            }}>
                                <h5 className="modal-title fw-bold text-white d-flex align-items-center gap-2">
                                    {modalMode === 'create' ? <RiAddLine className="fs-3" /> : <RiEditLine className="fs-3" />}
                                    {modalMode === 'create' ? 'Create New Role' : `Edit Role: ${formData.name}`}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setModalMode(null)}
                                />
                            </div>

                            <div className="modal-body p-4" style={{ overflowY: 'auto', flex: 1 }}>
                                    {/* Quick Role Presets */}
                                    <div 
                                        className="mb-4 p-3 rounded-3 border border-primary-subtle shadow-sm"
                                        style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)' }}
                                    >
                                        <small className="fw-bold text-primary d-flex align-items-center gap-1 d-block mb-2">
                                            ⚡ QUICK ROLE PRESETS:
                                        </small>
                                        <div className="d-flex flex-wrap gap-2">
                                            {[
                                                { name: 'HR Manager', codename: 'hr_manager', color: 'btn-primary' },
                                                { name: 'Immediate Manager', codename: 'immediate_manager', color: 'btn-success' },
                                                { name: 'MD', codename: 'md', color: 'btn-info text-white' },
                                                { name: 'Staff', codename: 'staff', color: 'btn-warning text-dark' },
                                            ].map((preset) => (
                                                <button
                                                    key={preset.codename}
                                                    type="button"
                                                    className={`btn btn-sm ${preset.color} fw-bold rounded-pill px-3 py-1 fs-12 shadow-sm`}
                                                    onClick={() => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            name: preset.name,
                                                            codename: preset.codename
                                                        }));
                                                    }}
                                                >
                                                    + {preset.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Role Basic Info */}
                                    <div className="row g-3 mb-4">
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold text-dark">
                                                Role Display Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-lg fs-15"
                                                placeholder="e.g. HR Manager, Immediate Manager"
                                                value={formData.name}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        name: val,
                                                        // auto fill codename if creating
                                                        codename: modalMode === 'create'
                                                            ? val.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_')
                                                            : prev.codename
                                                    }));
                                                }}
                                                required
                                                disabled
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-bold text-dark">
                                                Codename <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-lg fs-15 font-monospace"
                                                placeholder="e.g. hr_manager, immediate_manager"
                                                value={formData.codename}
                                                onChange={(e) => setFormData({ ...formData, codename: e.target.value })}
                                                required
                                                disabled
                                            />
                                            <small className="text-muted fs-12">System identifier (e.g. hr_manager, immediate_manager, md, staff).</small>
                                        </div>
                                    </div>

                                    {/* Permissions Picker Section */}
                                    <div className="border-top pt-3">
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                            <div>
                                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                                    <RiKey2Line className="text-primary" /> Assign Permissions
                                                    <span className="badge bg-primary rounded-pill ms-1">
                                                        {formData.permission_codenames.length} Selected
                                                    </span>
                                                </h6>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success font-semibold px-2 py-1 fs-12"
                                                    onClick={selectAllPermissions}
                                                >
                                                    Select All
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary font-semibold px-2 py-1 fs-12"
                                                    onClick={deselectAllPermissions}
                                                >
                                                    Deselect All
                                                </button>
                                            </div>
                                        </div>

                                        {/* Search Filter input */}
                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search permissions by name or codename..."
                                                value={permSearch}
                                                onChange={(e) => setPermSearch(e.target.value)}
                                            />
                                        </div>

                                        {/* Checkbox List */}
                                        <div
                                            className="p-3 bg-light rounded-3 border"
                                            style={{ maxHeight: '280px', overflowY: 'auto' }}
                                        >
                                            {filteredPermissions.length > 0 ? (
                                                <div className="row g-2">
                                                    {filteredPermissions.map((perm) => {
                                                        const isChecked = formData.permission_codenames.includes(perm.codename);
                                                        return (
                                                            <div key={perm.id} className="col-md-6">
                                                                <div
                                                                    className={`p-2 px-3 border rounded-3 d-flex align-items-center justify-content-between transition-all ${isChecked ? 'bg-primary-subtle border-primary' : 'bg-white'
                                                                        }`}
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => togglePermission(perm.codename)}
                                                                >
                                                                    <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input mt-0 flex-shrink-0"
                                                                            checked={isChecked}
                                                                            onChange={() => { }} // handled by parent div onClick
                                                                        />
                                                                        <div className="text-truncate">
                                                                            <div className="fw-semibold text-dark fs-13 text-truncate">{perm.name}</div>
                                                                            <code className="text-muted fs-11">{perm.codename}</code>
                                                                        </div>
                                                                    </div>
                                                                    {isChecked && (
                                                                        <span className="badge bg-primary flex-shrink-0">
                                                                            <RiCheckLine />
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <p className="text-center text-muted mb-0 py-3">No matching permissions found.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer bg-light p-3">
                                    <button
                                        type="button"
                                        className="btn btn-secondary fw-bold px-4"
                                        onClick={() => setModalMode(null)}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className={`btn fw-bold px-4 ${modalMode === 'create' ? 'btn-success' : 'btn-warning'}`}
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                                        ) : modalMode === 'create' ? 'Create Role' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                </div>
            )}
        </div>
    );
};

export default Roles;